import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import axios from 'axios';
import { claimsEdition } from '../BaseURL';

import HospitalClaims from './HospitalClaims';
import HospitalClaimsPremium from './HospitalClaimsPremium';

/**
 * Decides which Claims interface to open, then renders it.
 * ----------------------------------------------------------------------------
 * The server answers with the already-resolved edition, so the rule lives in
 * exactly one place (server.js, /claimsEdition):
 *
 *     premium  only when the facility is LICENSED in the central DB
 *              AND has SELECTED premium in System Settings
 *     default  in every other case
 *
 * Note what happens on failure: the catch falls through to 'default'. A licence
 * server that is down, a missing table, a dropped network — none of them may
 * stop a claims officer working. Losing the premium screen for a few minutes is
 * an inconvenience; losing Claims altogether stops the facility billing.
 *
 * Deliberately checked once per mount rather than cached in localStorage, so
 * revoking a licence takes effect the next time the screen is opened.
 */
export default function ClaimsSwitch() {
  const [edition, setEdition] = useState(null);   // null = still deciding

  useEffect(() => {
    let cancelled = false;

    axios.get(claimsEdition)
      .then((res) => {
        if (cancelled) return;
        setEdition(res.data?.edition === 'premium' ? 'premium' : 'default');
      })
      .catch(() => {
        if (!cancelled) setEdition('default');
      });

    return () => { cancelled = true; };
  }, []);

  if (edition === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return edition === 'premium' ? <HospitalClaimsPremium /> : <HospitalClaims />;
}
